import Papa, { type ParseResult } from 'papaparse'
import type { Contact } from '@otw/models/leads/contacts'
import type { PrivateUserInfoType } from '@otw/models'
import {
  CONTACT_EMAIL_PATTERN,
  contactMatchesEmail,
  formatContactDisplayName,
  normalizeContactEmail,
  normalizeContactName,
  normalizeContactPhoneDigits,
} from './contactIdentity'

export type ContactsCsvRowStatus = 'ready' | 'invalid' | 'duplicate'

export type MatchedContactOwnerKind = 'inventory' | 'sales'

/** Lightweight name+id shape for salesReps-style owner candidates. */
export interface ContactsCsvOwnerCandidate {
  id: string | number
  first_name?: string
  last_name?: string
  displayName?: string
  email?: string
}

const ownerDisplayName = (user: {
  first_name?: string | null
  last_name?: string | null
  displayName?: string | null
  email?: string | null
  id?: string | number
}): string => {
  const fullName = `${user.first_name || ''} ${user.last_name || ''}`.trim()
  return fullName || user.displayName || user.email || String(user.id ?? '')
}

/** Coerce numeric string ids (e.g. salesReps) back to numbers for ContactOwnerId storage. */
export const coerceContactOwnerId = (id: string | number): string | number => {
  const asString = String(id)
  const n = Number(asString)
  return Number.isFinite(n) && String(n) === asString ? n : asString
}

export interface ParsedContactsCsvRow {
  /** 1-based spreadsheet row number (header is row 1). */
  rowNumber: number
  name: string
  phone: string
  email: string
  /** Parsed from Last Activity Date when present; null if missing/unparseable. */
  lastActivityAt: Date | null
  /** Associated company name from the CSV (may be empty). */
  associatedCompany: string
  /** Raw Contact owner cell value (may be empty). */
  contactOwner: string
  /** Matched inventory or sales assignable user id, when Contact owner resolves. */
  matchedOwnerId?: string | number
  matchedOwnerKind?: MatchedContactOwnerKind
  status: ContactsCsvRowStatus
  skipReason?: string
  matchedContactId?: string
  matchedUserId?: string
}

export interface ParseContactsCsvResult {
  rows: ParsedContactsCsvRow[]
  readyCount: number
  duplicateCount: number
  invalidCount: number
}

export interface ParseContactsCsvContext {
  contacts: Contact[]
  users: PrivateUserInfoType[]
  salesOwners: PrivateUserInfoType[]
  inventoryOwners: PrivateUserInfoType[]
  /** Legacy salesReps constant entries — also eligible as sales owners. */
  salesReps?: ContactsCsvOwnerCandidate[]
}

const normalizeHeader = (value: string): string =>
  value.trim().toLowerCase().replace(/\s+/g, ' ')

const normalizePersonNameKey = (value: string): string =>
  value.trim().toLowerCase().replace(/\s+/g, ' ')

type ColumnKey =
  | 'name'
  | 'firstName'
  | 'lastName'
  | 'phone'
  | 'email'
  | 'lastActivityDate'
  | 'associatedCompany'
  | 'contactOwner'

const HEADER_ALIASES: Record<ColumnKey, string[]> = {
  name: ['name'],
  firstName: ['first name', 'firstname', 'first'],
  lastName: ['last name', 'lastname', 'last'],
  phone: ['number', 'phone', 'phone number', 'phone #', 'mobile'],
  email: ['email', 'e-mail', 'email address'],
  lastActivityDate: [
    'last activity date',
    'last activity',
    'last activity datetime',
    'last activity date/time',
  ],
  associatedCompany: [
    'associated company',
    'company',
    'company name',
    'associated company name',
  ],
  contactOwner: [
    'contact owner',
    'owner',
    'contact owner name',
    'assigned to',
  ],
}

const resolveColumnIndexes = (
  headerRow: string[],
): Partial<Record<ColumnKey, number>> => {
  const indexes: Partial<Record<ColumnKey, number>> = {}
  headerRow.forEach((raw, index) => {
    const normalized = normalizeHeader(raw ?? '')
    ;(Object.keys(HEADER_ALIASES) as ColumnKey[]).forEach((key) => {
      if (indexes[key] !== undefined) return
      if (HEADER_ALIASES[key].includes(normalized)) {
        indexes[key] = index
      }
    })
  })
  return indexes
}

const cellAt = (row: string[], index: number | undefined): string => {
  if (index === undefined) return ''
  return (row[index] ?? '').trim()
}

/**
 * Parse `YYYY-MM-DD HH:mm` or `YYYY-MM-DD` (local time). Returns null if unparseable.
 */
export const parseContactsCsvLastActivityDate = (raw: string): Date | null => {
  const trimmed = raw.trim()
  if (!trimmed) return null

  const withTime = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{1,2}):(\d{2})(?::(\d{2}))?$/.exec(
    trimmed,
  )
  if (withTime) {
    const year = Number(withTime[1])
    const month = Number(withTime[2]) - 1
    const day = Number(withTime[3])
    const hour = Number(withTime[4])
    const minute = Number(withTime[5])
    const second = withTime[6] !== undefined ? Number(withTime[6]) : 0
    const date = new Date(year, month, day, hour, minute, second)
    return Number.isNaN(date.getTime()) ? null : date
  }

  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(trimmed)
  if (dateOnly) {
    const year = Number(dateOnly[1])
    const month = Number(dateOnly[2]) - 1
    const day = Number(dateOnly[3])
    const date = new Date(year, month, day)
    return Number.isNaN(date.getTime()) ? null : date
  }

  return null
}

/**
 * Match a Contact owner display name: inventory assignable users first, then sales
 * (portal sales assignable users, then salesReps). At most one match is returned.
 */
export const matchContactOwnerFromCsv = (
  rawOwner: string,
  inventoryOwners: PrivateUserInfoType[],
  salesOwners: PrivateUserInfoType[],
  salesReps: ContactsCsvOwnerCandidate[] = [],
): { id: string | number; kind: MatchedContactOwnerKind } | null => {
  const key = normalizePersonNameKey(rawOwner)
  if (!key) return null

  const inventoryMatch = inventoryOwners.find(
    (user) => normalizePersonNameKey(ownerDisplayName(user)) === key,
  )
  if (inventoryMatch?.id !== undefined && inventoryMatch?.id !== null && inventoryMatch.id !== '') {
    return { id: coerceContactOwnerId(inventoryMatch.id), kind: 'inventory' }
  }

  const salesMatch = salesOwners.find(
    (user) => normalizePersonNameKey(ownerDisplayName(user)) === key,
  )
  if (salesMatch?.id !== undefined && salesMatch?.id !== null && salesMatch.id !== '') {
    return { id: coerceContactOwnerId(salesMatch.id), kind: 'sales' }
  }

  const salesRepMatch = salesReps.find(
    (rep) => normalizePersonNameKey(ownerDisplayName(rep)) === key,
  )
  if (salesRepMatch?.id !== undefined && salesRepMatch?.id !== null && salesRepMatch.id !== '') {
    return { id: coerceContactOwnerId(salesRepMatch.id), kind: 'sales' }
  }

  return null
}

/** Same matching rule as `findUserByEmail` in useUsers. */
const findPortalUserIdByEmail = (
  users: PrivateUserInfoType[],
  email: string,
): string | undefined => {
  const normalized = normalizeContactEmail(email)
  if (!normalized) return undefined
  const match = users.find(
    (user) => user.email && normalizeContactEmail(user.email) === normalized,
  )
  return match?.id
}

const findDuplicateContact = (
  name: string,
  email: string,
  phone: string,
  contacts: Contact[],
): Contact | null => {
  const normalizedEmail = email ? normalizeContactEmail(email) : ''
  const phoneDigits = phone ? normalizeContactPhoneDigits(phone) : ''

  if (normalizedEmail) {
    const byEmail = contacts.find((c) => contactMatchesEmail(c, normalizedEmail))
    if (byEmail) return byEmail
  }

  if (phoneDigits.length >= 7) {
    const byPhone = contacts.find(
      (c) => c.phone && normalizeContactPhoneDigits(c.phone) === phoneDigits,
    )
    if (byPhone) return byPhone
  }

  // Name-only rows (no email): match an existing contact by display name.
  const normalizedName = name ? normalizeContactName(name) : ''
  if (!normalizedEmail && normalizedName) {
    const byName = contacts.find(
      (c) => c.name && normalizeContactName(c.name) === normalizedName,
    )
    if (byName) return byName
  }

  return null
}

const parseCsvRawRows = (file: File): Promise<string[][]> =>
  new Promise((resolve, reject) => {
    Papa.parse<string[]>(file, {
      header: false,
      skipEmptyLines: 'greedy',
      complete: (result: ParseResult<string[]>) => resolve(result.data),
      error: (error) => reject(error),
    })
  })

const resolveRowName = (params: {
  legacyName: string
  firstName: string
  lastName: string
  email: string
  isActivityFormat: boolean
}): string => {
  if (params.isActivityFormat) {
    const combined = formatContactDisplayName(
      `${params.firstName} ${params.lastName}`.trim(),
    )
    if (combined) return combined
    if (params.email) return params.email.trim()
    return ''
  }
  return formatContactDisplayName(params.legacyName)
}

const classifyRow = (params: {
  rowNumber: number
  name: string
  phone: string
  email: string
  lastActivityAt: Date | null
  associatedCompany: string
  contactOwner: string
  matchedOwnerId?: string | number
  matchedOwnerKind?: MatchedContactOwnerKind
  contacts: Contact[]
  users: PrivateUserInfoType[]
  seenEmails: Map<string, number>
  seenPhones: Map<string, number>
  seenNames: Map<string, number>
}): ParsedContactsCsvRow => {
  const {
    rowNumber,
    name,
    phone,
    email,
    lastActivityAt,
    associatedCompany,
    contactOwner,
    matchedOwnerId,
    matchedOwnerKind,
    contacts,
    users,
    seenEmails,
    seenPhones,
    seenNames,
  } = params

  const base: Omit<ParsedContactsCsvRow, 'status' | 'skipReason' | 'matchedContactId' | 'matchedUserId'> = {
    rowNumber,
    name,
    phone,
    email,
    lastActivityAt,
    associatedCompany,
    contactOwner,
    ...(matchedOwnerId ? { matchedOwnerId } : {}),
    ...(matchedOwnerKind ? { matchedOwnerKind } : {}),
  }

  if (!name) {
    return {
      ...base,
      status: 'invalid',
      skipReason: 'Name is required',
    }
  }

  if (email && !CONTACT_EMAIL_PATTERN.test(email)) {
    return {
      ...base,
      status: 'invalid',
      skipReason: 'Invalid email format',
    }
  }

  const duplicate = findDuplicateContact(name, email, phone, contacts)
  if (duplicate) {
    return {
      ...base,
      status: 'duplicate',
      skipReason: 'Matches existing contact',
      matchedContactId: duplicate.id,
    }
  }

  const emailNorm = email ? normalizeContactEmail(email) : ''
  const phoneDigits = phone ? normalizeContactPhoneDigits(phone) : ''
  const nameNorm = !emailNorm ? normalizeContactName(name) : ''
  if (emailNorm && seenEmails.has(emailNorm)) {
    return {
      ...base,
      status: 'invalid',
      skipReason: `Duplicate of row ${seenEmails.get(emailNorm)} in this file`,
    }
  }
  if (phoneDigits.length >= 7 && seenPhones.has(phoneDigits)) {
    return {
      ...base,
      status: 'invalid',
      skipReason: `Duplicate of row ${seenPhones.get(phoneDigits)} in this file`,
    }
  }
  if (nameNorm && seenNames.has(nameNorm)) {
    return {
      ...base,
      status: 'invalid',
      skipReason: `Duplicate of row ${seenNames.get(nameNorm)} in this file`,
    }
  }

  if (emailNorm) seenEmails.set(emailNorm, rowNumber)
  if (phoneDigits.length >= 7) seenPhones.set(phoneDigits, rowNumber)
  if (nameNorm) seenNames.set(nameNorm, rowNumber)

  return {
    ...base,
    status: 'ready',
    matchedUserId: email ? findPortalUserIdByEmail(users, email) : undefined,
  }
}

/**
 * Parse a contacts CSV.
 * Supports legacy Name / Number / Email columns, and HubSpot-style
 * First Name / Last Name / Email / Last Activity Date / Associated Company / Contact owner.
 */
export async function parseContactsCsv(
  file: File,
  context: ParseContactsCsvContext,
): Promise<ParseContactsCsvResult> {
  const rawRows = await parseCsvRawRows(file)
  if (rawRows.length === 0) {
    throw new Error('The CSV file is empty.')
  }

  const headerRow = rawRows[0] ?? []
  const columnIndexes = resolveColumnIndexes(headerRow)

  const isActivityFormat =
    columnIndexes.firstName !== undefined || columnIndexes.lastName !== undefined
  const isLegacyFormat = columnIndexes.name !== undefined

  if (!isActivityFormat && !isLegacyFormat) {
    throw new Error(
      'Could not find name columns. Expected "Name", or "First Name" / "Last Name" (Email and other columns are optional).',
    )
  }

  const rows: ParsedContactsCsvRow[] = []
  const seenEmails = new Map<string, number>()
  const seenPhones = new Map<string, number>()
  const seenNames = new Map<string, number>()

  for (let i = 1; i < rawRows.length; i++) {
    const dataRow = rawRows[i] ?? []
    const rowNumber = i + 1
    const firstName = cellAt(dataRow, columnIndexes.firstName)
    const lastName = cellAt(dataRow, columnIndexes.lastName)
    const legacyName = cellAt(dataRow, columnIndexes.name)
    const phone = cellAt(dataRow, columnIndexes.phone)
    const email = cellAt(dataRow, columnIndexes.email)
    const associatedCompany = cellAt(dataRow, columnIndexes.associatedCompany)
    const contactOwner = cellAt(dataRow, columnIndexes.contactOwner)
    const lastActivityRaw = cellAt(dataRow, columnIndexes.lastActivityDate)
    const lastActivityAt = parseContactsCsvLastActivityDate(lastActivityRaw)

    const ownerMatch = matchContactOwnerFromCsv(
      contactOwner,
      context.inventoryOwners,
      context.salesOwners,
      context.salesReps ?? [],
    )

    const name = resolveRowName({
      legacyName,
      firstName,
      lastName,
      email,
      isActivityFormat,
    })

    if (
      !name &&
      !phone &&
      !email &&
      !associatedCompany &&
      !lastActivityRaw &&
      !contactOwner
    ) {
      continue
    }

    rows.push(
      classifyRow({
        rowNumber,
        name,
        phone,
        email,
        lastActivityAt,
        associatedCompany,
        contactOwner,
        matchedOwnerId: ownerMatch?.id,
        matchedOwnerKind: ownerMatch?.kind,
        contacts: context.contacts,
        users: context.users,
        seenEmails,
        seenPhones,
        seenNames,
      }),
    )
  }

  return {
    rows,
    readyCount: rows.filter((r) => r.status === 'ready').length,
    duplicateCount: rows.filter((r) => r.status === 'duplicate').length,
    invalidCount: rows.filter((r) => r.status === 'invalid').length,
  }
}

/** True when a duplicate row still has company linking and/or owner assignment work. */
export const contactsCsvRowHasFollowUpWork = (row: ParsedContactsCsvRow): boolean => {
  if (row.associatedCompany?.trim()) return true
  if (row.matchedOwnerId && row.matchedOwnerKind) return true
  return false
}

/**
 * Re-validate a single preview row after the user edits Name / Phone / Email.
 * Preserves lastActivityAt, associatedCompany, and owner fields from the original parse.
 */
export function revalidateContactsCsvRow(
  row: ParsedContactsCsvRow,
  contacts: Contact[],
  users: PrivateUserInfoType[],
  siblingReadyRows: ParsedContactsCsvRow[] = [],
): ParsedContactsCsvRow {
  const name = row.name.trim()
  const phone = row.phone.trim()
  const email = row.email.trim()
  const lastActivityAt = row.lastActivityAt ?? null
  const associatedCompany = row.associatedCompany?.trim() ?? ''
  const contactOwner = row.contactOwner?.trim() ?? ''
  const matchedOwnerId = row.matchedOwnerId
  const matchedOwnerKind = row.matchedOwnerKind

  const preservedOwner = {
    contactOwner,
    ...(matchedOwnerId ? { matchedOwnerId } : {}),
    ...(matchedOwnerKind ? { matchedOwnerKind } : {}),
  }

  if (!name) {
    return {
      ...row,
      name,
      phone,
      email,
      lastActivityAt,
      associatedCompany,
      ...preservedOwner,
      status: 'invalid',
      skipReason: 'Name is required',
      matchedContactId: undefined,
      matchedUserId: undefined,
    }
  }

  if (email && !CONTACT_EMAIL_PATTERN.test(email)) {
    return {
      ...row,
      name,
      phone,
      email,
      lastActivityAt,
      associatedCompany,
      ...preservedOwner,
      status: 'invalid',
      skipReason: 'Invalid email format',
      matchedContactId: undefined,
      matchedUserId: undefined,
    }
  }

  const duplicate = findDuplicateContact(name, email, phone, contacts)
  if (duplicate) {
    return {
      ...row,
      name,
      phone,
      email,
      lastActivityAt,
      associatedCompany,
      ...preservedOwner,
      status: 'duplicate',
      skipReason: 'Matches existing contact',
      matchedContactId: duplicate.id,
      matchedUserId: undefined,
    }
  }

  const emailNorm = email ? normalizeContactEmail(email) : ''
  const phoneDigits = phone ? normalizeContactPhoneDigits(phone) : ''
  const nameNorm = !emailNorm ? normalizeContactName(name) : ''
  const siblingDup = siblingReadyRows.find((other) => {
    if (other.rowNumber >= row.rowNumber) return false
    if (emailNorm && other.email && normalizeContactEmail(other.email) === emailNorm) {
      return true
    }
    if (
      phoneDigits.length >= 7 &&
      other.phone &&
      normalizeContactPhoneDigits(other.phone) === phoneDigits
    ) {
      return true
    }
    if (
      nameNorm &&
      !other.email?.trim() &&
      other.name &&
      normalizeContactName(other.name) === nameNorm
    ) {
      return true
    }
    return false
  })
  if (siblingDup) {
    return {
      ...row,
      name,
      phone,
      email,
      lastActivityAt,
      associatedCompany,
      ...preservedOwner,
      status: 'invalid',
      skipReason: `Duplicate of row ${siblingDup.rowNumber} in this file`,
      matchedContactId: undefined,
      matchedUserId: undefined,
    }
  }

  return {
    ...row,
    name,
    phone,
    email,
    lastActivityAt,
    associatedCompany,
    ...preservedOwner,
    status: 'ready',
    skipReason: undefined,
    matchedContactId: undefined,
    matchedUserId: email ? findPortalUserIdByEmail(users, email) : undefined,
  }
}
